import { agreementGrantsRelation, type AgreementPermission } from './agreementPermissions'
import { billGrantsRelation, type BillPermission } from './billPermissions'
import { CONFIG_AREAS, type ConfigArea } from './configAreas'
import { CORE_DATA_DIMENSIONS, type CoreDataDimension } from './coreDataDimensions'
import { mapUserToOrganisationRole, type UserRoleTierConfig } from './organisationRoles'
import { type Pool } from './pools'
import { requestGrantsRelation, type RequestPermission } from './requestPermissions'
import { DEFAULT_SUPPLIER_PERMISSIONS, supplierGrantsRelation, type SupplierPermission } from './supplierPermissions'
import { workflowGrantsRelation, type WorkflowPermission } from './workflowPermissions'
import type { FgaAssignableRelation, FgaObjectRef, FgaObjectType, FgaRelation, FgaTuple } from './generated/model'
import type { GrantFixedLevel } from './grantFixedSets'
import type { PermissionSetEntity } from './permissionSetEntities'

// ---------------------------------------------------------------------------
// Object references
// ---------------------------------------------------------------------------

/**
 * Builds an object-reference string (`type:id`) for any model object type.
 *
 * The id is lowercased: OpenFGA matches object refs as exact strings while Postgres compares uuids
 * case-insensitively, so a caller-supplied uppercase uuid would otherwise address a different
 * object than the synced tuples. Every id in the model is a uuid or a lowercase composite, and
 * every write goes through these builders too, so normalizing here keeps checks and tuples on one
 * canonical form.
 */
export const objectRef = <T extends FgaObjectType>(type: T, id: string): FgaObjectRef<T> => `${type}:${id.toLowerCase()}`

/** The id half of an object reference. A ref carrying no type prefix is already an id. */
export const objectIdOf = (ref: string): string => ref.slice(ref.indexOf(':') + 1)

/** The type half of an object reference, or '' for a ref carrying no type prefix. */
export const objectTypeOf = (ref: string): string => (ref.includes(':') ? ref.slice(0, ref.indexOf(':')) : '')

export const organisationObject = (orgId: string): FgaObjectRef<'organisation'> => objectRef('organisation', orgId)

export const configAreaObject = (orgId: string, area: ConfigArea): FgaObjectRef<'config_area'> =>
  objectRef('config_area', `${orgId}/${area}`)

export const groupObject = (groupId: string): FgaObjectRef<'group'> => objectRef('group', groupId)

export const requestObject = (requestId: string): FgaObjectRef<'request'> => objectRef('request', requestId)

export const supplierObject = (supplierId: string): FgaObjectRef<'supplier'> => objectRef('supplier', supplierId)

export const agreementObject = (agreementId: string): FgaObjectRef<'agreement'> => objectRef('agreement', agreementId)

export const billObject = (billId: string): FgaObjectRef<'bill'> => objectRef('bill', billId)

export const workflowObject = (workflowId: string): FgaObjectRef<'workflow'> => objectRef('workflow', workflowId)

/** A pool's object id: the pool is part of it, so no other feature's pool shares it. */
export const poolObjectId = (orgId: string, pool: Pool): string => `${orgId}/${pool}`

export const poolObject = (orgId: string, pool: Pool): FgaObjectRef<'pool'> => objectRef('pool', poolObjectId(orgId, pool))

export const renewalObject = (renewalId: string): FgaObjectRef<'renewal'> => objectRef('renewal', renewalId)

export const documentObject = (documentId: string): FgaObjectRef<'document'> => objectRef('document', documentId)

export const roleObject = (roleId: string): FgaObjectRef<'role'> => objectRef('role', roleId)

/** A scoping dimension instance — one per (organisation, dimension). */
export const scopeDimensionObject = (orgId: string, dimension: CoreDataDimension): FgaObjectRef<'scope_dimension'> =>
  objectRef('scope_dimension', `${orgId}/${dimension}`)

/** A scope value — the id is the departments.id / subsidiaries.id row uuid. */
export const scopeValueObject = (valueId: string): FgaObjectRef<'scope_value'> => objectRef('scope_value', valueId)

/**
 * Everything that differs between the entities, in one place. Every per-entity lookup reads
 * from here, so adding an entity is one entry and a missing one is a compile error — the alternative
 * is a scattered literal, and a read against the wrong object type comes back EMPTY rather than
 * throwing, so the set would silently report no grants.
 */
/** An object type paired with one of its own relations, for a "holds this on any such record" lookup. */
type RecordCreateQuery = { [T in FgaObjectType]: { type: T; relation: FgaRelation<T> } }[FgaObjectType]

export const GRANT_MODEL_BY_ENTITY = {
  request: {
    grantType: 'request_grant',
    permissionSetType: 'request_permission_set',
    /** Where the entity's grants hang off a shared anchor, and off the record itself. */
    anchoredRelation: 'request_grant',
    scope: { valueRelation: 'request_grant', dynamicRelation: 'matching_request_grant' },
    orgWideViewRelation: 'can_view_all_requests',
    orgCreateRelation: 'can_create_request',
    // A request is created from a workflow, so its create right can also sit on the workflow itself.
    recordCreateQuery: { type: 'workflow', relation: 'can_create_request' },
    clearanceRelation: 'confidential_request_access',
    recordRelation: 'grant',
    recordType: 'request',
  },
  supplier: {
    grantType: 'supplier_grant',
    permissionSetType: 'supplier_permission_set',
    anchoredRelation: 'supplier_grant',
    scope: { valueRelation: 'supplier_grant', dynamicRelation: 'matching_supplier_grant' },
    orgWideViewRelation: 'can_view_all_suppliers',
    orgCreateRelation: 'can_create_supplier',
    recordCreateQuery: undefined,
    clearanceRelation: 'confidential_supplier_access',
    recordRelation: 'grant',
    recordType: 'supplier',
  },
  agreement: {
    grantType: 'agreement_grant',
    permissionSetType: 'agreement_permission_set',
    anchoredRelation: 'agreement_grant',
    scope: { valueRelation: 'agreement_grant', dynamicRelation: 'matching_agreement_grant' },
    orgWideViewRelation: 'can_view_all_agreements',
    orgCreateRelation: 'can_add_agreement_all_suppliers',
    // An agreement is added to a supplier, so its create right can also sit on the supplier itself.
    recordCreateQuery: { type: 'supplier', relation: 'can_add_agreement' },
    clearanceRelation: 'confidential_agreement_access',
    recordRelation: 'grant',
    recordType: 'agreement',
  },
  bill: {
    grantType: 'bill_grant',
    permissionSetType: 'bill_permission_set',
    anchoredRelation: 'bill_grant',
    scope: undefined, // present so a generic GRANT_MODEL_BY_ENTITY[entity] access types across every entity
    orgWideViewRelation: 'can_view_all_bills',
    orgCreateRelation: undefined,
    recordCreateQuery: undefined,
    clearanceRelation: undefined,
    recordRelation: 'grant',
    recordType: 'bill',
  },
  workflow: {
    grantType: 'workflow_grant',
    permissionSetType: 'workflow_permission_set',
    anchoredRelation: 'workflow_grant',
    scope: undefined,
    orgWideViewRelation: undefined,
    orgCreateRelation: undefined,
    recordCreateQuery: undefined,
    clearanceRelation: undefined,
    recordRelation: 'grant',
    recordType: 'workflow',
  },
  // The mapped constraint correlates each entry with its OWN record type (the entity name is the
  // record type), so recordRelation is checked against the right relation list per entity — a
  // hand-widened intersection would silently stop checking an entity someone forgot to add to it.
} as const satisfies {
  [Entity in PermissionSetEntity]: {
    grantType: FgaObjectType
    permissionSetType: FgaObjectType
    anchoredRelation: FgaRelation<'organisation'>
    /** Both arms or neither: an entity with a scope value has a matching dynamic-dimension arm too. */
    scope?: {
      valueRelation: FgaRelation<'organisation'> & FgaRelation<'scope_value'>
      dynamicRelation: FgaRelation<'scope_dimension'>
    }
    orgWideViewRelation?: FgaRelation<'organisation'>
    orgCreateRelation?: FgaRelation<'organisation'>
    /** Where the create right can also be held below the organisation — the record it is added to. */
    recordCreateQuery?: RecordCreateQuery
    clearanceRelation?: FgaAssignableRelation<'organisation'>
    recordRelation: FgaRelation<Entity>
    recordType: Entity
  }
}

/** Entities whose entry declares a `clearanceRelation` — i.e. can hold confidential records. */
export type ClearanceEntity = {
  [Entity in PermissionSetEntity]: (typeof GRANT_MODEL_BY_ENTITY)[Entity]['clearanceRelation'] extends string ? Entity : never
}[PermissionSetEntity]

export const CLEARANCE_ENTITIES = (Object.keys(GRANT_MODEL_BY_ENTITY) as PermissionSetEntity[]).filter(
  (entity): entity is ClearanceEntity => GRANT_MODEL_BY_ENTITY[entity].clearanceRelation != null
)

export const grantObject = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grantId: string
): FgaObjectRef<(typeof GRANT_MODEL_BY_ENTITY)[Entity]['grantType']> =>
  objectRef(GRANT_MODEL_BY_ENTITY[entity].grantType, grantId)

/**
 * The id carries no organisation, so callers must resolve it through an org-scoped metadata read
 * before naming it in a tuple — a ref alone cannot be confined to one tenant.
 */
export const permissionSetObject = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissionSetId: string
): FgaObjectRef<(typeof GRANT_MODEL_BY_ENTITY)[Entity]['permissionSetType']> =>
  objectRef(GRANT_MODEL_BY_ENTITY[entity].permissionSetType, permissionSetId)

// ---------------------------------------------------------------------------
// Permission sets — what a grant may confer
// ---------------------------------------------------------------------------

export const buildPermissionSetAnchorTuple = (entity: PermissionSetEntity, orgId: string, permissionSetId: string): FgaTuple => ({
  user: organisationObject(orgId),
  relation: 'structural_organisation',
  object: permissionSetObject(entity, permissionSetId),
})

/**
 * The subject every permission-set toggle and fixed grant slot names: the organisation's members.
 * Naming the organisation rather than `user:*` keeps each marker reachable from its own tenant only;
 * a wildcard is reachable from every tenant's lists, and costs each of them for every other one.
 */
export const organisationMembers = (orgId: string): `${FgaObjectRef<'organisation'>}#derived_member` =>
  `${organisationObject(orgId)}#derived_member`

// These stay per-entity: FgaTuple correlates relation with object type, and a generic version cannot
// prove a request permission lands on a request set without a cast.

/** The tuple enabling one permission on a request set. Its absence is what "not granted" means. */
export const requestPermissionTuple = (orgId: string, permissionSetId: string, permission: RequestPermission): FgaTuple => ({
  user: organisationMembers(orgId),
  relation: requestGrantsRelation(permission),
  object: permissionSetObject('request', permissionSetId),
})

/** The tuple enabling one permission on a supplier set. */
export const supplierPermissionTuple = (orgId: string, permissionSetId: string, permission: SupplierPermission): FgaTuple => ({
  user: organisationMembers(orgId),
  relation: supplierGrantsRelation(permission),
  object: permissionSetObject('supplier', permissionSetId),
})

export const agreementPermissionTuple = (orgId: string, permissionSetId: string, permission: AgreementPermission): FgaTuple => ({
  user: organisationMembers(orgId),
  relation: agreementGrantsRelation(permission),
  object: permissionSetObject('agreement', permissionSetId),
})

export const billPermissionTuple = (orgId: string, permissionSetId: string, permission: BillPermission): FgaTuple => ({
  user: organisationMembers(orgId),
  relation: billGrantsRelation(permission),
  object: permissionSetObject('bill', permissionSetId),
})

export const workflowPermissionTuple = (orgId: string, permissionSetId: string, permission: WorkflowPermission): FgaTuple => ({
  user: organisationMembers(orgId),
  relation: workflowGrantsRelation(permission),
  object: permissionSetObject('workflow', permissionSetId),
})

/**
 * A set carrying {@link DEFAULT_SUPPLIER_PERMISSIONS} — see there for why this is not what the API
 * provisions.
 */
export const buildDefaultSupplierPermissionSetTuples = (orgId: string, permissionSetId: string): FgaTuple[] => [
  buildPermissionSetAnchorTuple('supplier', orgId, permissionSetId),
  ...DEFAULT_SUPPLIER_PERMISSIONS.map((permission) => supplierPermissionTuple(orgId, permissionSetId, permission)),
]

// ---------------------------------------------------------------------------
// Grants — a subject, what it confers, and where it applies
// ---------------------------------------------------------------------------

/**
 * Exactly one of a fixed set or an organisation-authored one, never both: a custom set may confer LESS
 * than Viewer, so unioning them would make the reduced set unreachable. The model cannot enforce that
 * exclusivity — this union is what makes a combination unrepresentable.
 */
export type GrantConfiguration = { kind: 'fixed'; level: GrantFixedLevel } | { kind: 'custom'; permissionSetId: string }

/**
 * The subject and permission source of one grant. A custom set id must be org-resolved first; a fixed
 * level is anchored on the granting organisation's members, as a custom set's toggles are on its own.
 */
export const buildGrantTuples = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grant: { orgId: string; grantId: string; subject: string; configuration: GrantConfiguration }
): FgaTuple[] => {
  const object = grantObject(entity, grant.grantId)
  return [
    { user: grant.subject, relation: 'subject', object },
    grant.configuration.kind === 'fixed'
      ? { user: organisationMembers(grant.orgId), relation: `${grant.configuration.level}_set`, object }
      : { user: permissionSetObject(entity, grant.configuration.permissionSetId), relation: 'custom_set', object },
  ]
}

/** Where a grant applies. `record` is the "Involved Only" scope on suppliers, direct access on requests. */
export type GrantScope =
  | { kind: 'organisation'; orgId: string }
  | { kind: 'value'; valueId: string }
  | { kind: 'dynamic'; orgId: string; dimension: CoreDataDimension }
  | { kind: 'record'; entityId: string }

/**
 * The scope edge attaching a grant where it applies. Relations come from {@link GRANT_MODEL_BY_ENTITY} rather
 * than a second copy: a divergence would not throw — an unmatched relation reads as "no scope", so the
 * grant silently vanishes from every read instead of failing.
 */
export const buildGrantScopeTuple = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grantId: string,
  scope: GrantScope
): FgaTuple => {
  const user = grantObject(entity, grantId)
  const entityConfig = GRANT_MODEL_BY_ENTITY[entity]
  switch (scope.kind) {
    case 'organisation':
      return { user, relation: entityConfig.anchoredRelation, object: organisationObject(scope.orgId) }
    case 'value':
      if (entityConfig.scope == null) throw new Error(`${entity} grants cannot be value-scoped`)
      return { user, relation: entityConfig.scope.valueRelation, object: scopeValueObject(scope.valueId) }
    case 'dynamic':
      if (entityConfig.scope == null) throw new Error(`${entity} grants cannot be dynamically scoped`)
      return { user, relation: entityConfig.scope.dynamicRelation, object: scopeDimensionObject(scope.orgId, scope.dimension) }
    case 'record':
      return {
        user,
        relation: entityConfig.recordRelation,
        object: objectRef(entityConfig.recordType, scope.entityId),
      }
  }
}

/** Every tuple one grant holds: its subject, what it confers, and where it applies. */
export const buildScopedGrantTuples = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grant: { orgId: string; grantId: string; subject: string; configuration: GrantConfiguration },
  scope: GrantScope
): FgaTuple[] => [...buildGrantTuples(entity, grant), buildGrantScopeTuple(entity, grant.grantId, scope)]

/**
 * The floor and ceiling an admin can put a user on for one supplier, consulting no permission set;
 * anything between them needs a grant. Not `derived_` prefixed on purpose — convergence deletes
 * unexpected `derived_` tuples, and an admin's grant is never in the expected set.
 *
 * Ordered narrowest first: a user may hold several at once, and readers collapse that to the widest.
 */
export const SUPPLIER_DIRECT_LEVELS = ['viewer', 'owner'] as const

export type SupplierDirectLevel = (typeof SUPPLIER_DIRECT_LEVELS)[number]

export const buildSupplierDirectGrantTuple = (grant: {
  supplierId: string
  subject: string
  level: SupplierDirectLevel
}): FgaTuple => ({ user: grant.subject, relation: grant.level, object: supplierObject(grant.supplierId) })

// ---------------------------------------------------------------------------
// Derived entity tuples — what the sync pipeline converges
// ---------------------------------------------------------------------------

/**
 * Everything a request must supply to derive its tuples. Every edge field is required: the requests service's
 * `RequestAuthzState` is this type, so a reader that drops one fails to compile rather than deleting that edge on
 * every sync.
 */
export type RequestTupleInput = {
  id: string
  isConfidential: boolean
  requestorUserId: string
  collaboratorUserIds: readonly string[]
  collaboratorGroupIds: readonly string[]
  /** Owners of each RFx step's latest round — the floor of `can_manage_sourcing`. */
  rfxOwnerUserIds: readonly string[]
  /** Core-data value ids the request is scoped to (department incl. renewal fallback, buyer legal entity). */
  scopeValueIds: readonly string[]
  /** requests.request_supplier, when set — routes the supplier's view_related_requests to the request. */
  supplierId: string | null
  agreementIds: readonly string[]
}

/**
 * The derived tuples a live request holds. Shared by the scanner and sweeper so the
 * derivation lives in one place.
 *
 * A confidential record carries the `_confidential` twins of its organisation and scope edges
 * INSTEAD of the normal ones. Every broad access arm hangs off the normal edges, so the swap is
 * what cuts broad access; the twins are read only by the `conf_` relations (permission AND
 * confidential_*_access), which is what lets clearance holders back in at their existing level.
 */
export const buildRequestTuples = (orgId: string, request: RequestTupleInput): FgaTuple[] => {
  const object = requestObject(request.id)
  const organisationRelation = request.isConfidential ? 'derived_organisation_confidential' : 'derived_organisation'
  const scopeRelation = request.isConfidential ? 'derived_scope_confidential' : 'derived_scope'
  // The supplier hop carries the supplier's broad arms, so it swaps like the other two.
  const supplierRelation = request.isConfidential ? 'derived_supplier_confidential' : 'derived_supplier'
  const agreementRelation = request.isConfidential ? 'derived_agreement_confidential' : 'derived_agreement'
  return [
    { user: organisationObject(orgId), relation: organisationRelation, object },
    { user: `user:${request.requestorUserId}`, relation: 'derived_requester', object },
    ...request.collaboratorUserIds.map((userId): FgaTuple => ({
      user: `user:${userId}`,
      relation: 'derived_collaborator',
      object,
    })),
    ...request.collaboratorGroupIds.map((groupId): FgaTuple => ({
      user: `${groupObject(groupId)}#derived_member`,
      relation: 'derived_collaborator',
      object,
    })),
    ...request.rfxOwnerUserIds.map((userId): FgaTuple => ({
      user: `user:${userId}`,
      relation: 'derived_rfx_owner',
      object,
    })),
    ...request.scopeValueIds.map((valueId): FgaTuple => ({
      user: scopeValueObject(valueId),
      relation: scopeRelation,
      object,
    })),
    ...(request.supplierId != null
      ? [{ user: supplierObject(request.supplierId), relation: supplierRelation, object } satisfies FgaTuple]
      : []),
    ...request.agreementIds.map((agreementId): FgaTuple => ({
      user: agreementObject(agreementId),
      relation: agreementRelation,
      object,
    })),
  ]
}

/**
 * Everything a supplier must supply to derive its tuples. Every edge field is required: the suppliers service's
 * `SupplierAuthzState` is built from this type, so a reader that drops one fails to compile.
 */
export type SupplierTupleInput = {
  id: string
  isConfidential: boolean
  assignments: readonly { userId: string; kind: string }[]
  /**
   * Core-data value ids the supplier is scoped to (subsidiaries of its live organisation_supplier_profiles
   * rows, and the departments of its live engagements and live agreements).
   */
  scopeValueIds: readonly string[]
  agreementIds: readonly string[]
  parentId: string | null
}

/**
 * The derived tuples a live supplier holds. Shared by the scanner, sweeper and seeder so
 * none can drift from the others.
 *
 * Admin-granted access is deliberately absent — re-deriving `viewer`/`owner` or grants here would
 * have convergence treat them as drift.
 */
export const buildSupplierTuples = (orgId: string, supplier: SupplierTupleInput): FgaTuple[] => {
  const object = supplierObject(supplier.id)
  // A confidential record swaps its organisation and scope edges for the twins — see buildRequestTuples.
  const organisationRelation = supplier.isConfidential ? 'derived_organisation_confidential' : 'derived_organisation'
  const scopeRelation = supplier.isConfidential ? 'derived_scope_confidential' : 'derived_scope'
  const agreementRelation = supplier.isConfidential ? 'derived_agreement_confidential' : 'derived_agreement'
  const parentRelation = supplier.isConfidential ? 'derived_parent_confidential' : 'derived_parent'
  // The kind no longer decides what an assignee may do, so two rows for one user collapse to one tuple.
  const assigneeUserIds = [...new Set(supplier.assignments.map((assignment) => assignment.userId))]
  return [
    { user: organisationObject(orgId), relation: organisationRelation, object },
    ...assigneeUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_contact', object })),
    ...supplier.scopeValueIds.map((valueId): FgaTuple => ({
      user: scopeValueObject(valueId),
      relation: scopeRelation,
      object,
    })),
    ...supplier.agreementIds.map((agreementId): FgaTuple => ({
      user: agreementObject(agreementId),
      relation: agreementRelation,
      object,
    })),
    ...(supplier.parentId != null
      ? [{ user: supplierObject(supplier.parentId), relation: parentRelation, object } satisfies FgaTuple]
      : []),
  ]
}

/**
 * Everything an agreement must supply to derive its tuples. Every edge field is required: the agreements service's
 * `AgreementAuthzState` is this type, so a reader that drops one fails to compile.
 */
export type AgreementTupleInput = {
  id: string
  isConfidential: boolean
  /** agreements.owner_id ∪ agreement_additional_owners, deduped here so callers need not. */
  ownerUserIds: readonly string[]
  /** agreements.supplier_id — NOT NULL, so exactly one edge; routes the supplier's view_related_agreements. */
  supplierId: string
  parentId: string | null
  /**
   * Core-data value ids the agreement is scoped to (agreements.subsidiary_id, when set, and the primary
   * department of each live term).
   */
  scopeValueIds: readonly string[]
}

/**
 * The derived tuples a live agreement holds. Shared by the scanner, sweeper and seeder so
 * none can drift from the others.
 */
export const buildAgreementTuples = (orgId: string, agreement: AgreementTupleInput): FgaTuple[] => {
  const object = agreementObject(agreement.id)
  // A confidential record swaps its organisation and scope edges for the twins — see buildRequestTuples.
  const organisationRelation = agreement.isConfidential ? 'derived_organisation_confidential' : 'derived_organisation'
  const scopeRelation = agreement.isConfidential ? 'derived_scope_confidential' : 'derived_scope'
  const supplierRelation = agreement.isConfidential ? 'derived_supplier_confidential' : 'derived_supplier'
  const parentRelation = agreement.isConfidential ? 'derived_parent_confidential' : 'derived_parent'
  const ownerUserIds = [...new Set(agreement.ownerUserIds)]
  return [
    { user: organisationObject(orgId), relation: organisationRelation, object },
    { user: supplierObject(agreement.supplierId), relation: supplierRelation, object },
    ...ownerUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_owner', object })),
    ...(agreement.parentId != null
      ? [{ user: agreementObject(agreement.parentId), relation: parentRelation, object } satisfies FgaTuple]
      : []),
    ...agreement.scopeValueIds.map((valueId): FgaTuple => ({
      user: scopeValueObject(valueId),
      relation: scopeRelation,
      object,
    })),
  ]
}

/** Everything a bill must supply to derive its tuples. The bills service's `BillAuthzState` is this type. */
export type BillTupleInput = {
  id: string
  approverUserIds: readonly string[]
  draftAssigneeUserId: string | null
  isInTeamQueue: boolean
}

/**
 * The derived tuples a live bill holds. Shared by the scanner and sweeper so neither can drift
 * from the other.
 *
 * Bill carries no confidential variant and no `derived_scope` relation, so this needs neither the
 * edge swap nor a scope edge that {@link buildRequestTuples} and {@link buildAgreementTuples} carry.
 */
export const buildBillTuples = (orgId: string, bill: BillTupleInput): FgaTuple[] => {
  const object = billObject(bill.id)
  // One user can be assigned to two APPROVE_BILL tasks on the same bill, unlike task ids.
  const approverUserIds = [...new Set(bill.approverUserIds)]
  return [
    { user: organisationObject(orgId), relation: 'derived_organisation', object },
    ...approverUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_approver', object })),
    ...(bill.draftAssigneeUserId == null
      ? []
      : [{ user: `user:${bill.draftAssigneeUserId}`, relation: 'derived_draft_assignee', object } satisfies FgaTuple]),
    ...(bill.isInTeamQueue
      ? [{ user: poolObject(orgId, 'bill_draft_assignee'), relation: 'derived_pool', object } satisfies FgaTuple]
      : []),
  ]
}

/** The derived tuples a live workflow holds. */
export const buildWorkflowTuples = (orgId: string, workflow: { id: string }): FgaTuple[] => [
  { user: organisationObject(orgId), relation: 'derived_organisation', object: workflowObject(workflow.id) },
]

/** The granted tuple restricting a workflow to its grantees; its absence is what "open" means. */
export const workflowRestrictedTuple = (orgId: string, workflowId: string): FgaTuple => ({
  user: organisationMembers(orgId),
  relation: 'restricted',
  object: workflowObject(workflowId),
})

/** Everything an organisation's Draft Assignee pool must supply to derive its tuples. */
export type BillDraftAssigneePoolTupleInput = {
  memberUserIds: readonly string[]
  memberGroupIds: readonly string[]
}

export const buildBillDraftAssigneePoolTuples = (orgId: string, pool: BillDraftAssigneePoolTupleInput): FgaTuple[] => {
  const object = poolObject(orgId, 'bill_draft_assignee')
  return [
    ...pool.memberUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_member', object })),
    ...pool.memberGroupIds.map((groupId): FgaTuple => ({
      user: `${groupObject(groupId)}#derived_member`,
      relation: 'derived_member',
      object,
    })),
  ]
}

export type DocumentParentRef = { id: string; isConfidential: boolean; isDeleted: boolean }

export type DocumentTupleInput = {
  id: string
  isGlobal: boolean
  isMarkedConfidential: boolean
  supplier: DocumentParentRef | null
  agreements: readonly DocumentParentRef[]
  request: DocumentParentRef | null
  uploaderUserId: string | null
  markedByUserId: string | null
}

export const buildDocumentTuples = (orgId: string, input: DocumentTupleInput): FgaTuple[] => {
  // A request-only document is a placeholder nothing reads through FGA, so it holds no tuples and costs no list.
  if (!input.isGlobal) return []
  const { id, isMarkedConfidential, supplier, agreements, request, uploaderUserId, markedByUserId } = input
  const edge = (relation: FgaAssignableRelation<'document'>, user: string): FgaTuple => ({
    user,
    relation,
    object: documentObject(id),
  })
  const parents = [
    ...(supplier == null ? [] : [{ ...supplier, tuple: edge('derived_supplier', supplierObject(supplier.id)) }]),
    ...agreements.map((agreement) => ({ ...agreement, tuple: edge('derived_agreement', agreementObject(agreement.id)) })),
    ...(request == null ? [] : [{ ...request, tuple: edge('derived_request', requestObject(request.id)) }]),
  ]

  const hasConfidentialParent = parents.some((parent) => parent.isConfidential)
  const isRestricted = isMarkedConfidential || hasConfidentialParent
  // A marked document keeps no parent edge. Otherwise a confidential parent suppresses every open one, so a parent's
  // edge stays only when it is itself confidential, or when no parent is. A confidential parent counts even when
  // deleted, so a document under a removed confidential parent stays restricted, not org-wide.
  const keptParents = isMarkedConfidential
    ? []
    : parents.filter((parent) => !parent.isDeleted && (parent.isConfidential || !hasConfidentialParent))
  return [
    ...keptParents.map(({ tuple }) => tuple),
    ...(uploaderUserId == null ? [] : [edge('derived_uploader', `user:${uploaderUserId}`)]),
    ...(markedByUserId == null ? [] : [edge('derived_marker', `user:${markedByUserId}`)]),
    edge(isRestricted ? 'derived_organisation_confidential' : 'derived_organisation', organisationObject(orgId)),
    ...(isRestricted ? [edge('derived_confidential', 'user:*')] : []),
  ]
}

export type RenewalTupleInput = {
  id: string
  /** `deleted` when the term or the agreement is, which reaches nothing; `none` when there is no agreement term. */
  agreement: { status: 'live'; id: string } | { status: 'deleted' } | { status: 'none' }
  assigneeUserIds: readonly string[]
}

export const buildRenewalTuples = (orgId: string, renewal: RenewalTupleInput): FgaTuple[] => {
  const edge = (relation: FgaAssignableRelation<'renewal'>, user: string): FgaTuple => ({
    user,
    relation,
    object: renewalObject(renewal.id),
  })
  switch (renewal.agreement.status) {
    case 'live':
      return [edge('derived_agreement', agreementObject(renewal.agreement.id))]
    case 'deleted':
      return []
    case 'none':
      return [
        edge('derived_organisation', organisationObject(orgId)),
        ...[...new Set(renewal.assigneeUserIds)].map((userId) => edge('derived_assignee', `user:${userId}`)),
      ]
  }
}

/**
 * The tuple marking a user's role-derived organisation relation (their legacy role tier;
 * owner for super admins), or null when the user holds none: suppliers, and users whose access
 * comes from a custom role instead (see {@link mapUserToOrganisationRole}).
 */
export const organisationRoleTuple = (orgId: string, user: UserRoleTierConfig & { id: string }): FgaTuple | null => {
  const role = mapUserToOrganisationRole(user)
  return role == null ? null : { user: `user:${user.id}`, relation: role, object: organisationObject(orgId) }
}

/**
 * The tuple making a user a member of their organisation — what every permission-set toggle and fixed
 * grant slot is anchored on. Unlike {@link organisationRoleTuple} it ignores custom roles: a custom-role
 * holder has no tier but must still pass the anchor of the grants that role holds. Null for suppliers.
 */
export const organisationMemberTuple = (
  orgId: string,
  user: Pick<UserRoleTierConfig, 'role'> & { id: string }
): FgaTuple | null =>
  user.role === 'supplier' ? null : { user: `user:${user.id}`, relation: 'derived_member', object: organisationObject(orgId) }

/** The membership tuple placing a user in their custom role, derived from users.custom_role_id. */
export const roleAssigneeTuple = (userId: string, roleId: string): FgaTuple => ({
  user: `user:${userId}`,
  relation: 'derived_assignee',
  object: roleObject(roleId),
})

/** The membership tuple placing a group's members in its custom role, derived from groups.custom_role_id. */
export const groupRoleAssigneeTuple = (groupId: string, roleId: string): FgaTuple => ({
  user: `${groupObject(groupId)}#derived_member`,
  relation: 'derived_assignee',
  object: roleObject(roleId),
})

/** The member tuple placing a user in an org group. */
export const groupMemberTuple = (userId: string, groupId: string): FgaTuple => ({
  user: `user:${userId}`,
  relation: 'derived_member',
  object: groupObject(groupId),
})

/**
 * The dimension link is kept whether or not the row is archived, so fixed grants stay resolvable;
 * the member tuples are not, which is what makes matching grants on an archived value go dark.
 */
export const buildCoreDataValueTuples = (
  orgId: string,
  value: { id: string; dimension: CoreDataDimension; isArchived: boolean; memberUserIds: readonly string[] }
): FgaTuple[] => {
  const object = scopeValueObject(value.id)
  return [
    { user: scopeDimensionObject(orgId, value.dimension), relation: 'structural_dimension', object },
    ...(value.isArchived
      ? []
      : value.memberUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_member', object }))),
  ]
}

/**
 * The derived_member tuples for a user's own core-data links (users.department_id,
 * users.subsidiary_id). Nulls filtered by the caller passing only present ids.
 */
export const buildUserScopeMembershipTuples = (userId: string, valueIds: readonly string[]): FgaTuple[] =>
  valueIds.map((valueId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_member', object: scopeValueObject(valueId) }))

// ---------------------------------------------------------------------------
// Structural anchors — bootstrap data, ensured rather than derived
// ---------------------------------------------------------------------------

/** One config_area instance per area. Ensured idempotently, never deleted, not mutable through the API. */
export const buildAnchorTuples = (orgId: string): FgaTuple[] => {
  const org = organisationObject(orgId)
  return CONFIG_AREAS.map((area): FgaTuple => ({
    user: org,
    relation: 'structural_organisation',
    object: configAreaObject(orgId, area),
  }))
}

/** One scope_dimension instance per dimension, on the same ensure-only terms as {@link buildAnchorTuples}. */
export const buildScopeDimensionAnchorTuples = (orgId: string): FgaTuple[] => {
  const org = organisationObject(orgId)
  return CORE_DATA_DIMENSIONS.map((dimension): FgaTuple => ({
    user: org,
    relation: 'structural_organisation',
    object: scopeDimensionObject(orgId, dimension),
  }))
}
